import { Platform } from 'react-native';

// Digital Sanctuary Design System v1.2.0
// =============================================================================
// KINGDOM BIBLE QUIZ — DESIGN SYSTEM TOKENS
// "The Digital Sanctuary" — Indigo + Gold palette
// =============================================================================

// ---------------------------------------------------------------------------
// COLORS
// ---------------------------------------------------------------------------

export const colors = {
  // Primary — Indigo
  primary: '#4F46E5',
  primaryDark: '#4338CA',
  primaryLight: '#6366F1',
  primaryContainer: '#E0E7FF',
  onPrimary: '#FFFFFF',
  onPrimaryContainer: '#312E81',

  // Accent — Gold / Amber
  accent: '#F59E0B',
  accentDark: '#D97706',
  accentLight: '#FBBF24',
  accentContainer: '#FEF3C7',
  onAccent: '#FFFFFF',
  onAccentContainer: '#78350F',

  // Success / Correct — Emerald
  correct: '#10B981',
  correctDark: '#059669',
  correctLight: '#34D399',
  correctContainer: '#D1FAE5',
  onCorrect: '#FFFFFF',
  onCorrectContainer: '#064E3B',

  // Error / Incorrect — Red
  incorrect: '#EF4444',
  incorrectDark: '#DC2626',
  incorrectLight: '#F87171',
  incorrectContainer: '#FEE2E2',
  onIncorrect: '#FFFFFF',
  onIncorrectContainer: '#7F1D1D',

  // Neutrals — Slate (warm-tinted, never pure black)
  surface: '#FAF8FF',
  surfaceDim: '#F1F0F7',
  surfaceContainerLow: '#F5F3FA',
  surfaceContainer: '#EEEDF5',
  surfaceContainerHigh: '#E8E6EF',
  surfaceContainerHighest: '#E2E0EA',
  onSurface: '#1D1A23',
  onSurfaceVariant: '#464555',
  outline: '#777587',
  outlineVariant: '#C7C4D8',

  // Background
  background: '#FAF8FF',
  onBackground: '#1D1A23',

  // Inverse
  inverseSurface: '#322F38',
  inverseOnSurface: '#F6EEFB',
  inversePrimary: '#C3C0FF',

  // Fixed utility
  white: '#FFFFFF',
  black: '#1D1A23', // Never use #000000
  transparent: 'transparent',

  // Gradient presets
  gradientPrimary: ['#3525CD', '#4F46E5'] as const,
  gradientAccent: ['#F59E0B', '#FBBF24'] as const,
  gradientCorrect: ['#059669', '#10B981'] as const,
  gradientSurface: ['#FAF8FF', '#F5F3FA'] as const,
} as const;

// Dark mode variants
export const colorsDark = {
  primary: '#C3C0FF',
  primaryDark: '#A5A1FF',
  primaryLight: '#E2DFFF',
  primaryContainer: '#3730A3',
  onPrimary: '#1E1B4B',
  onPrimaryContainer: '#E0E7FF',

  accent: '#FBBF24',
  accentDark: '#F59E0B',
  accentLight: '#FDE68A',
  accentContainer: '#78350F',
  onAccent: '#422006',
  onAccentContainer: '#FEF3C7',

  correct: '#34D399',
  correctDark: '#10B981',
  correctLight: '#6EE7B7',
  correctContainer: '#064E3B',
  onCorrect: '#022C22',
  onCorrectContainer: '#D1FAE5',

  incorrect: '#F87171',
  incorrectDark: '#EF4444',
  incorrectLight: '#FCA5A5',
  incorrectContainer: '#7F1D1D',
  onIncorrect: '#450A0A',
  onIncorrectContainer: '#FEE2E2',

  surface: '#1D1A23',
  surfaceDim: '#161320',
  surfaceContainerLow: '#252230',
  surfaceContainer: '#2D2A38',
  surfaceContainerHigh: '#383540',
  surfaceContainerHighest: '#433F48',
  onSurface: '#F6EEFB',
  onSurfaceVariant: '#C7C4D8',
  outline: '#918F9E',
  outlineVariant: '#464555',

  background: '#1D1A23',
  onBackground: '#F6EEFB',

  inverseSurface: '#F6EEFB',
  inverseOnSurface: '#322F38',
  inversePrimary: '#4F46E5',

  white: '#FFFFFF',
  black: '#1D1A23',
  transparent: 'transparent',

  gradientPrimary: ['#4F46E5', '#6366F1'] as const,
  gradientAccent: ['#D97706', '#F59E0B'] as const,
  gradientCorrect: ['#10B981', '#34D399'] as const,
  gradientSurface: ['#1D1A23', '#252230'] as const,
} as const;

// ---------------------------------------------------------------------------
// TYPOGRAPHY
// ---------------------------------------------------------------------------

export const typography = {
  display: {
    fontSize: 28,
    fontWeight: '700' as const,
    lineHeight: 34,
    fontFamily: Platform.select({ web: 'Manrope, system-ui, sans-serif', default: undefined }),
  },
  heading: {
    fontSize: 24,
    fontWeight: '700' as const,
    lineHeight: 30,
    fontFamily: Platform.select({ web: 'Manrope, system-ui, sans-serif', default: undefined }),
  },
  title: {
    fontSize: 20,
    fontWeight: '600' as const,
    lineHeight: 26,
    fontFamily: Platform.select({ web: 'Manrope, system-ui, sans-serif', default: undefined }),
  },
  body: {
    fontSize: 16,
    fontWeight: '400' as const,
    lineHeight: 24,
    fontFamily: Platform.select({ web: 'system-ui, sans-serif', default: undefined }),
  },
  label: {
    fontSize: 14,
    fontWeight: '500' as const,
    lineHeight: 20,
    letterSpacing: 0.5,
    fontFamily: Platform.select({ web: 'system-ui, sans-serif', default: undefined }),
  },
  caption: {
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16,
    fontFamily: Platform.select({ web: 'system-ui, sans-serif', default: undefined }),
  },
  micro: {
    fontSize: 10,
    fontWeight: '400' as const,
    lineHeight: 14,
    fontFamily: Platform.select({ web: 'system-ui, sans-serif', default: undefined }),
  },
} as const;

// ---------------------------------------------------------------------------
// SPACING
// ---------------------------------------------------------------------------

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
  '5xl': 48,
  '6xl': 64,
} as const;

// ---------------------------------------------------------------------------
// BORDER RADIUS
// ---------------------------------------------------------------------------

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 9999,
} as const;

// ---------------------------------------------------------------------------
// SHADOWS — Platform-specific
// ---------------------------------------------------------------------------

export const shadows = {
  // Ambient shadows per Digital Sanctuary spec: blur 24-40px, opacity 4-6%, tinted with on_surface
  sm: Platform.select({
    ios: {
      shadowColor: '#1D1A23',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.04,
      shadowRadius: 12,
    },
    android: { elevation: 2 },
    web: { boxShadow: '0 2px 12px rgba(29, 26, 35, 0.04)' },
    default: {},
  }),
  md: Platform.select({
    ios: {
      shadowColor: '#1D1A23',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.05,
      shadowRadius: 24,
    },
    android: { elevation: 4 },
    web: { boxShadow: '0 4px 24px rgba(29, 26, 35, 0.05)' },
    default: {},
  }),
  lg: Platform.select({
    ios: {
      shadowColor: '#1D1A23',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.06,
      shadowRadius: 40,
    },
    android: { elevation: 8 },
    web: { boxShadow: '0 8px 40px rgba(29, 26, 35, 0.06)' },
    default: {},
  }),
} as const;

// ---------------------------------------------------------------------------
// ANIMATION TOKENS
// ---------------------------------------------------------------------------

export const animation = {
  quick: 120,
  normal: 200,
  slow: 400,

  // Reanimated spring configs
  spring: {
    gentle: { damping: 15, stiffness: 150, mass: 1 },
    bouncy: { damping: 12, stiffness: 200, mass: 0.8 },
    snappy: { damping: 20, stiffness: 300, mass: 0.6 },
  },
} as const;

// ---------------------------------------------------------------------------
// QUIZ-SPECIFIC TOKENS
// ---------------------------------------------------------------------------

export const quiz = {
  // Answer option states
  // Ghost border: outlineVariant at 15% opacity per Stitch "No-Line" rule
  ghostBorder: 'rgba(199, 196, 216, 0.15)',

  option: {
    default: {
      background: colors.white,
      border: 'rgba(199, 196, 216, 0.15)', // Ghost border — felt, not seen
      text: colors.onSurface,
    },
    selected: {
      background: colors.primaryContainer,
      border: colors.primary,
      text: colors.onPrimaryContainer,
    },
    correct: {
      background: colors.correctContainer,
      border: colors.correct,
      text: colors.onCorrectContainer,
    },
    incorrect: {
      background: colors.incorrectContainer,
      border: colors.incorrect,
      text: colors.onIncorrectContainer,
    },
    disabled: {
      background: colors.surfaceContainerHigh,
      border: colors.outlineVariant,
      text: colors.outline,
    },
  },

  // Timer bar color thresholds
  timer: {
    full: colors.correct,
    half: colors.accent,
    low: colors.incorrect,
  },

  // Badge colors (A, B, C, D)
  badge: {
    default: colors.surfaceContainerHighest,
    selected: colors.primary,
    correct: colors.correct,
    incorrect: colors.incorrect,
  },

  // Minimum tap target (accessibility)
  minTapTarget: 48,
} as const;
// v1.2.0 Digital Sanctuary
